import { Button } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import { MODES_REGLEMENTS } from '@/constants';
import { postBordereauVersement, postDetailNumeraireBordereau, printBordereau } from '@/routes';
import type { BordereauType, ModeReglementsPointageType, ReglementType } from '@/types';
import { getLibelleByCode } from '@/utils';
import ModalGenerationBordereau from './ModalGenerationBordereau';

type ButtonGenerationBordereauProps = {
  selectedReglements: ReglementType[];
  modeReglementActif: ModeReglementsPointageType;
  onGenerate: () => void;
};

const ButtonGenerationBordereau = (props: ButtonGenerationBordereauProps) => {
  const { selectedReglements, modeReglementActif, onGenerate } = props;

  const [generateModalOpen, setGenerateModalOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const handleGenerateButtonClick = React.useCallback(() => {
    setGenerateModalOpen(true);
  }, []);

  const handlePrintBordereau = React.useCallback(
    (identifiant: number) =>
      request
        .get(printBordereau(identifiant), {
          headers: { Accept: 'application/json, application/pdf' },
        })
        .then((response: any) => {
          if (response.url) {
            window.open(response.url, '_blank');
          } else {
            const blob = response.data || response;
            const pdfBlob = new Blob([blob], { type: 'application/pdf' });
            const url = URL.createObjectURL(pdfBlob);
            window.open(url, '_blank');
            setTimeout(() => {
              URL.revokeObjectURL(url);
            }, 2000);
          }
        })
        .catch(() => {
          toast("Une erreur est survenue lors de l'ouverture du bordereau", { type: 'error' });
        }),
    [],
  );

  const handleBordereauGenerate = React.useCallback(
    (repartitionNumeraire: Record<string, unknown>[]) => {
      setLoading(true);

      request
        .post(postBordereauVersement(), {
          modeReglement: modeReglementActif,
          identifiantsReglements: _.map(selectedReglements, 'identifiant'),
        })
        .then(({ identifiant }: BordereauType) => {
          if (!_.isNil(repartitionNumeraire)) {
            const repartitionNumeraireSansZero = _.filter(
              repartitionNumeraire,
              (item) => (item.nombre as number) > 0,
            );

            request
              .post(
                postDetailNumeraireBordereau(identifiant),
                _.map(repartitionNumeraireSansZero, ({ valeur, nombre }) => ({
                  valeur,
                  nombre,
                })),
              )
              .then(() => handlePrintBordereau(identifiant))
              .finally(() => {
                setLoading(false);
                setGenerateModalOpen(false);
                onGenerate();
              });
          } else {
            handlePrintBordereau(identifiant).finally(() => {
              setLoading(false);
              setGenerateModalOpen(false);
              onGenerate();
            });
          }
        });
    },
    [modeReglementActif, selectedReglements, onGenerate, handlePrintBordereau],
  );

  const totalLibelle = React.useMemo(() => {
    const reglementsValides = _.filter(selectedReglements, (reglement) =>
      _.isNil(reglement.dateAnnulationRecu),
    );

    if (
      modeReglementActif === MODES_REGLEMENTS.CESU ||
      modeReglementActif === MODES_REGLEMENTS.CHEQUE_VAC
    ) {
      return _.sumBy(reglementsValides, 'nombre');
    }

    return _.size(reglementsValides);
  }, [modeReglementActif, selectedReglements]);

  const reglementsValides = React.useMemo(
    () => _.filter(selectedReglements, (reglement) => _.isNil(reglement.dateAnnulationRecu)),
    [selectedReglements],
  );

  const libelleBordereau = React.useMemo(
    () => `bordereau de remise
    ${modeReglementActif !== MODES_REGLEMENTS.NUMERAIRE && !_.isEmpty(reglementsValides) ? ' pour' : ''}
    ${modeReglementActif !== MODES_REGLEMENTS.NUMERAIRE && !_.isEmpty(reglementsValides) ? totalLibelle : ''}
    ${
      modeReglementActif !== MODES_REGLEMENTS.NUMERAIRE && !_.isEmpty(reglementsValides)
        ? getLibelleByCode(modeReglementActif, totalLibelle > 1)
        : ''
    }
    `,
    [modeReglementActif, reglementsValides, totalLibelle],
  );

  return (
    <>
      <Button
        data-testid="button-generation-bordereaux"
        style={{ alignSelf: 'flex-end', marginRight: '1em' }}
        size="large"
        positive
        content={`Générer le ${libelleBordereau}`}
        disabled={_.isEmpty(reglementsValides) || loading}
        loading={loading}
        onClick={handleGenerateButtonClick}
      />
      {generateModalOpen && (
        <ModalGenerationBordereau
          onCancel={() => setGenerateModalOpen(false)}
          onConfirm={handleBordereauGenerate}
          modeReglementActif={modeReglementActif}
          totalMode={totalLibelle}
          montantTotal={parseFloat(_.sumBy(reglementsValides, 'montant').toFixed(2))}
          loading={loading}
        />
      )}
    </>
  );
};

export default ButtonGenerationBordereau;
